import { useEffect } from 'react';

import { CheckCircleOutlined, CloseCircleOutlined } from '@ant-design/icons';
import { Col, Row } from 'antd';
import { useSelector } from 'react-redux';
import { DefinitionConstant } from 'src/constants/common.constants';
import { useAccountSelector } from 'src/hooks';
import { useFindMinimalDepositAmountForInvestment } from 'src/hooks/useFindMinimalDepositAmountForInvestment';
import { MFormCurrencyField, MTypography } from 'src/lib';
import CardComponent from 'src/lib/Miscellaneous/Card';
import { OfferOrderIntent } from 'src/models';
import { Color } from 'src/styles';
import { getAccountFundingInstructions } from 'src/utils';

import * as Styles from './CheckPayment.styles';

export interface CheckPaymentProps {
  isValid: (value: boolean) => void;
}

export const CheckPayment = ({ isValid }: CheckPaymentProps) => {
  const { account } = useAccountSelector();
  const offerOrderIntent: OfferOrderIntent = useSelector((state: any) => state.offerOrders.intent);
  const minimalDepositAmount = useFindMinimalDepositAmountForInvestment();

  const renderOfferCheckInstructions = () => (
    <>
      <MTypography
        variant='body1'
        weight='bold'
        color={Color.Gray8}
        className={Styles.subTitle}
        testId='check-transfer-instructions'>
        Make check(s) payable to:
      </MTypography>
      <MTypography variant='body1' color={Color.Gray7} className={Styles.paragraph}>
        Apex Clearing FBO, {getAccountFundingInstructions(account)}
      </MTypography>
      <MTypography variant='body1' weight='bold' color={Color.Gray8} className={Styles.subTitle}>
        Mail checks to:
      </MTypography>
      <MTypography variant='body1' color={Color.Gray7} className={Styles.paragraph}>
        <span>TradingBlock</span>
        <br />
        <span>Attn. Cashiering</span>
        <br />
        <span>311 S Wacker Dr, Suite 1775</span>
        <br />
        <span>Chicago, IL 60606</span>
      </MTypography>
    </>
  );

  const renderExternalOfferCheckInstructions = () => (
    <>
      <MTypography
        variant='body1'
        weight='bold'
        color={Color.Gray8}
        className={Styles.subTitle}
        testId='check-transfer-instructions'>
        Make check(s) payable to:
      </MTypography>
      <MTypography variant='body1' color={Color.Gray7} className={Styles.paragraph}>
        {offerOrderIntent.checkInstructions?.payableTo}
      </MTypography>
      <MTypography variant='body1' weight='bold' color={Color.Gray8} className={Styles.subTitle}>
        Mail checks to:
      </MTypography>
      <MTypography variant='body1' color={Color.Gray7} className={Styles.paragraph}>
        <span>{offerOrderIntent.checkInstructions?.bankName}</span>
        <br />
        <span>
          {offerOrderIntent.checkInstructions?.address.address1}{' '}
          {offerOrderIntent.checkInstructions?.address.address2
            ? `, ${offerOrderIntent.checkInstructions?.address.address2}`
            : ''}
        </span>
        <br />
        <span>
          {offerOrderIntent.checkInstructions?.address.city}, {offerOrderIntent.checkInstructions?.address.state}{' '}
          {offerOrderIntent.checkInstructions?.address.postalCode}
        </span>
      </MTypography>
    </>
  );

  useEffect(() => {
    if (!offerOrderIntent.isExternal && !account?.accountNumber) {
      isValid(false);

      return;
    }
    isValid(true);
  }, []);

  return (
    <div className={Styles.container} data-testid={'confirm-order-modal-check'}>
      <CardComponent className={Styles.card}>
        {!offerOrderIntent.isExternal && (
          <Col span={24}>
            <MFormCurrencyField
              label='Cash Available'
              value={offerOrderIntent.cashAvailable}
              tooltip={{ type: 'popover', content: DefinitionConstant.CASH_AVAILABLE, icon: 'question' }}
              testId={'check-cash-available'}
            />
          </Col>
        )}
        <Col span={24}>
          <MFormCurrencyField label='Transfer Amount' value={minimalDepositAmount} testId={'check-transfer-amount'} />
        </Col>
        {offerOrderIntent.isExternal ? renderExternalOfferCheckInstructions() : renderOfferCheckInstructions()}
      </CardComponent>

      <Row className={Styles.acceptableAndUnaccaptableRow} justify='space-between'>
        <Col span={11} xs={24} md={11} className={Styles.checkInfoCol}>
          <Row>
            <Col span={24}>
              <CheckCircleOutlined className={Styles.checkIcon} />
            </Col>
            <Col span={24}>
              <MTypography variant='body2' weight='bold' color={Color.Gray8} testId='confirm-order-acceptable-checks'>
                Acceptable Checks
              </MTypography>
            </Col>
            <Col span={24}>
              <ul className={Styles.acceptableAndUnaccaptableList}>
                <li>
                  <MTypography variant='body2' color={Color.Gray7}>
                    Checks must be payable in U.S. dollars and through a U.S. bank.
                  </MTypography>
                </li>
                <li>
                  <MTypography variant='body2' color={Color.Gray7}>
                    Personal checks must be drawn from a bank account in an account owner’s name and must include
                    &quot;Jr&quot; or &quot;Sr&quot; if applicable.
                  </MTypography>
                </li>
                {offerOrderIntent.isExternal ? (
                  <li>
                    <MTypography variant='body2' color={Color.Gray7}>
                      If you are investing as an individual a check needs to come from a individual or joint bank
                      account, not from a Trust or other entity type.
                    </MTypography>
                  </li>
                ) : (
                  <>
                    <li>
                      <MTypography variant='body2' color={Color.Gray7}>
                        Checks from joint checking accounts may be deposited into either checking account owner’s My IPO
                        account.
                      </MTypography>
                    </li>
                    <li>
                      <MTypography variant='body2' color={Color.Gray7}>
                        Checks from an individual checking account may be deposited into a My IPO joint account if that
                        person is one of the account owners.
                      </MTypography>
                    </li>
                    <li>
                      <MTypography variant='body2' color={Color.Gray7}>
                        Investment Club checks should be drawn from a checking account in the name of the Investment
                        Club. If a member of the Investment Club remits a personal check, the check must be payable to:
                        &quot;Apex Clearing / FBO the Investment Club name.&quot; (&quot;FBO&quot; stands for &quot;For
                        the Benefit Of&quot;)
                      </MTypography>
                    </li>
                  </>
                )}
              </ul>
            </Col>
          </Row>
        </Col>
        <Col span={11} xs={24} md={11} className={Styles.checkInfoCol}>
          <Row>
            <Col span={24}>
              <CloseCircleOutlined className={Styles.closeIcon} />
            </Col>
            <Col span={24}>
              <MTypography variant='body2' weight='bold' color={Color.Gray8} testId='confirm-order-unacceptable-checks'>
                Unacceptable Checks
              </MTypography>
            </Col>
            <Col span={24}>
              <ul className={Styles.acceptableAndUnaccaptableList}>
                <li>
                  <MTypography variant='body2' color={Color.Gray7}>
                    Cashier’s check
                  </MTypography>
                </li>
                <li>
                  <MTypography variant='body2' color={Color.Gray7}>
                    Money Orders
                  </MTypography>
                </li>
                <li>
                  <MTypography variant='body2' color={Color.Gray7}>
                    Foreign instruments
                  </MTypography>
                </li>
                <li>
                  <MTypography variant='body2' color={Color.Gray7}>
                    Thrift withdrawal orders
                  </MTypography>
                </li>
                <li>
                  <MTypography variant='body2' color={Color.Gray7}>
                    Domestic drafts
                  </MTypography>
                </li>
                <li>
                  <MTypography variant='body2' color={Color.Gray7}>
                    Checks that have been double-endorsed (checks with more than one signature on the back)
                  </MTypography>
                </li>
                <li>
                  <MTypography variant='body2' color={Color.Gray7}>
                    Third-party checks{' '}
                    {offerOrderIntent.isExternal
                      ? ''
                      : 'not properly made out and endorsed per the rules stated in the "Acceptable Deposits" section above'}
                  </MTypography>
                </li>
                <li>
                  <MTypography variant='body2' color={Color.Gray7}>
                    Checks from minors
                  </MTypography>
                </li>
                <li>
                  <MTypography variant='body2' color={Color.Gray7}>
                    Check dated over six months old
                  </MTypography>
                </li>
                <li>
                  <MTypography variant='body2' color={Color.Gray7}>
                    Travelers checks
                  </MTypography>
                </li>
                <li>
                  <MTypography variant='body2' color={Color.Gray7}>
                    Credit card checks
                  </MTypography>
                </li>
              </ul>
            </Col>
          </Row>
        </Col>
      </Row>
    </div>
  );
};
